import { useEffect, useState } from "react";
import axios from "axios";
import { Save, Mail, CreditCard, Loader2, Eye, EyeOff, CheckCircle2, AlertCircle, MessageCircle, Bell, Clock, Trophy, Activity, Zap, AlertTriangle, ShieldAlert, Send, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { TrustedDevicesPanel } from "@/components/TrustedDevicesPanel";
import { CurrencyAuditPanel } from "./CurrencyAuditPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function HealthChip({ label, ok, missing, hint }) {
    return (
        <div data-testid={`health-${label.toLowerCase().replace(/\s+/g, "-")}`} className={`rounded-xl border px-3 py-2.5 ${ok ? "border-emerald-200 bg-emerald-50/40" : "border-rose-200 bg-rose-50/40"}`}>
            <div className="flex items-center gap-2">
                {ok ? <CheckCircle2 size={14} className="text-emerald-600" /> : <AlertCircle size={14} className="text-rose-600" />}
                <span className="text-[12.5px] font-medium text-slate-900">{label}</span>
                <span className={`ml-auto text-[10px] font-mono-tr tracking-wider uppercase ${ok ? "text-emerald-700" : "text-rose-700"}`}>{ok ? "ready" : "off"}</span>
            </div>
            {hint && <div className="mt-1 text-[11.5px] text-slate-500">{hint}</div>}
            {!ok && Array.isArray(missing) && missing.length > 0 && (
                <div className="mt-1 text-[11px] text-rose-700/80">Missing: {missing.join(", ")}</div>
            )}
        </div>
    );
}

function SettingField({ label, name, value, onChange, placeholder, secret = false, hint }) {
    const [show, setShow] = useState(false);
    const realType = secret && !show ? "password" : "text";
    return (
        <label className="block">
            <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500 mb-1.5">{label}</div>
            <div className="relative">
                <input
                    data-testid={`setting-${name}`}
                    type={realType}
                    value={value || ""}
                    placeholder={placeholder}
                    onChange={(e) => onChange(name, e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none text-[14px] font-mono-tr pr-10"
                />
                {secret && (
                    <button type="button" onClick={() => setShow((v) => !v)} data-testid={`setting-${name}-eye`} aria-label={show ? `Hide ${label}` : `Show ${label}`} className="absolute end-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                        {show ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                )}
            </div>
            {hint && <div className="mt-1 text-[12px] text-slate-500">{hint}</div>}
        </label>
    );
}

export default function Settings() {
    const { user } = useAuth();
    const [s, setS] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testEmail, setTestEmail] = useState("");
    const [testing, setTesting] = useState(false);
    const [testWa, setTestWa] = useState("");
    const [testingWa, setTestingWa] = useState(false);
    const [health, setHealth] = useState(null);

    const loadHealth = async () => {
        try {
            const { data } = await axios.get(`${API}/admin/settings/integration-status`);
            setHealth(data);
        } catch (e) {
            // Non-critical secondary fetch. Surface in dev tools but don't toast the admin.
            console.warn("integration-status fetch failed:", e?.message || e);
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/admin/settings`);
                setS(data);
                await loadHealth();
            } catch (e) {
                toast.error("Failed to load settings");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const change = (k, v) => setS((prev) => ({ ...prev, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await axios.put(`${API}/admin/settings`, s);
            setS(data);
            toast.success("Settings saved");
            await loadHealth();
        } catch (e) {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    const testSmtp = async () => {
        if (!testEmail) return toast.error("Enter a test recipient");
        setTesting(true);
        try {
            const { data } = await axios.post(`${API}/admin/settings/test-smtp`, { to: testEmail });
            const via = data.provider ? ` via ${data.provider.toUpperCase()}` : "";
            if (data.sent) toast.success(`Test email sent${via} — check your inbox`);
            else toast.error(data.error || "Send failed — check email settings", { duration: 12000 });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Send failed");
        } finally {
            setTesting(false);
        }
    };

    const testWhatsapp = async () => {
        if (!testWa) return toast.error("Enter a test phone (E.164, e.g. +97455060499)");
        setTestingWa(true);
        try {
            const { data } = await axios.post(`${API}/admin/settings/test-whatsapp`, { to: testWa });
            const via = data.provider && data.provider !== "none" ? ` via ${data.provider}` : "";
            if (data.sent) toast.success(`Test WhatsApp sent${via} — check your phone`);
            else toast.error(`Send failed${via} — credentials missing or invalid`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Send failed");
        } finally {
            setTestingWa(false);
        }
    };

    if (loading) return <div className="flex items-center justify-center min-h-[40vh]"><Loader2 className="animate-spin text-slate-500" /></div>;

    return (
        <div className="space-y-6" data-testid="admin-settings">
            {/* Notifications health-check banner */}
            {health && (
                <div data-testid="notif-health" className="rounded-2xl border border-slate-200 bg-white p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <Activity size={16} className="text-cyan-600" />
                        <h3 className="font-display text-[16px] tracking-tight text-slate-900">Notification health</h3>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-[13px]">
                        <HealthChip label={`Email (${health.email.active_provider || (health.email.configured ? "configured" : "off")})`} ok={health.email.configured} missing={health.email.missing} />
                        <HealthChip label={`WhatsApp (${health.whatsapp.active_provider || (health.whatsapp.configured ? "configured" : "off")})`} ok={health.whatsapp.configured} missing={health.whatsapp.configured ? [] : ["callmebot_apikey or twilio creds"]} />
                        <HealthChip label="Stripe payments" ok={health.stripe.configured} />
                        <HealthChip label="Daily snapshot" ok={health.daily_snapshot.configured} hint={health.daily_snapshot.recipients.length ? `${health.daily_snapshot.recipients.length} recipient(s)` : "Add email(s) below"} />
                    </div>
                    {(!health.email.configured || !health.whatsapp.configured) && (
                        <div className="mt-3 text-[12.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                            <strong>Heads up:</strong> notifications won't be delivered until the missing credentials below are saved.
                            {!health.email.configured && health.email.missing.length > 0 && <> Email needs: <code>{health.email.missing.join(", ")}</code>.</>}
                            {!health.whatsapp.configured && health.whatsapp.missing.length > 0 && <> WhatsApp needs: <code>{health.whatsapp.missing.join(", ")}</code>.</>}
                        </div>
                    )}
                </div>
            )}

            <SectionCard
                icon={Send}
                title="Resend (recommended · HTTP email · works in production)"
                hint="Primary email transport. Uses port 443 (HTTPS) so it works even where SMTP is blocked. Get an API key at https://resend.com/api-keys (free tier: 3,000 emails/month)."
                status={s.resend_api_key ? "connected" : "disconnected"}
            >
                <div className="grid sm:grid-cols-2 gap-4">
                    <SettingField
                        label="Resend API key"
                        name="resend_api_key"
                        value={s.resend_api_key}
                        onChange={change}
                        placeholder="re_xxxxxxxxxxxxxxxxxxxxxxx"
                        secret
                        hint="Create at https://resend.com/api-keys"
                    />
                    <SettingField
                        label="From address"
                        name="resend_from"
                        value={s.resend_from}
                        onChange={change}
                        placeholder="Toothrocket <noreply@toothrocket.com>"
                        hint="Use Toothrocket <onboarding@resend.dev> for instant testing (sandbox — only sends to your own Resend-account email). To send to real customers, verify your domain at https://resend.com/domains and use noreply@yourdomain.com."
                    />
                </div>
                <div className="mt-4 text-[12.5px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 leading-relaxed">
                    <strong className="text-slate-900">Why Resend?</strong> Production hosting typically blocks outbound SMTP ports (25, 465, 587),
                    which is why Gmail SMTP fails on the live site while working in preview. Resend delivers over HTTPS (port 443) so it works everywhere.
                    While the &ldquo;from&rdquo; domain is unverified, Resend only lets you send to the email address that owns the API key — perfect for testing, useless for customers. Verify <code>toothrocket.com</code> at
                    <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-cyan-600 underline mx-1">resend.com/domains</a>
                    to unlock sending to any recipient.
                </div>
            </SectionCard>

            <SectionCard
                icon={Mail}
                title="SMTP / Email (legacy fallback)"
                hint="Optional Gmail Workspace SMTP as a fallback if Resend fails. Skipped entirely when Resend is configured. Ignored in production (SMTP ports blocked by infra)."
            >
                <div className="grid sm:grid-cols-2 gap-4">
                    <SettingField label="SMTP host" name="smtp_host" value={s.smtp_host} onChange={change} placeholder="smtp.gmail.com" />
                    <SettingField label="SMTP port" name="smtp_port" value={s.smtp_port} onChange={change} placeholder="587" />
                    <SettingField label="SMTP user" name="smtp_user" value={s.smtp_user} onChange={change} placeholder="orders@toothrocket.com" />
                    <SettingField label="SMTP password / App Password" name="smtp_password" value={s.smtp_password} onChange={change} placeholder="••••••••••••••••" secret hint="Generate at https://myaccount.google.com/apppasswords" />
                    <SettingField label="From header" name="smtp_from" value={s.smtp_from} onChange={change} placeholder="Toothrocket Labs <orders@toothrocket.com>" />
                    <SettingField label="Orders inbox" name="orders_inbox" value={s.orders_inbox} onChange={change} placeholder="orders@toothrocket.com" />
                </div>
                <div className="mt-5 pt-5 border-t border-slate-100 flex flex-wrap items-end gap-3">
                    <label className="block flex-1 min-w-[220px]">
                        <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500 mb-1.5">Test recipient</div>
                        <input
                            data-testid="smtp-test-recipient"
                            type="email"
                            value={testEmail}
                            onChange={(e) => setTestEmail(e.target.value)}
                            placeholder="you@yourclinic.com"
                            className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-cyan-500 outline-none text-[14px]"
                        />
                    </label>
                    <button onClick={testSmtp} disabled={testing} data-testid="smtp-test-btn" className="tr-btn-ghost !py-2.5">
                        {testing ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                        Send test email
                    </button>
                </div>
            </SectionCard>

            <SectionCard
                icon={CreditCard}
                title="Razorpay (India · INR / UPI / cards)"
                hint="Get keys from Razorpay Dashboard → Settings → API Keys. Use TEST mode keys (rzp_test_*) to start."
                status={s.razorpay_key_id && s.razorpay_key_secret ? "connected" : "disconnected"}
            >
                <div className="grid sm:grid-cols-2 gap-4">
                    <SettingField label="Key ID" name="razorpay_key_id" value={s.razorpay_key_id} onChange={change} placeholder="rzp_test_xxxxxxxxxxxxxx" />
                    <SettingField label="Key Secret" name="razorpay_key_secret" value={s.razorpay_key_secret} onChange={change} placeholder="••••••••••••" secret />
                    <SettingField label="Webhook secret (optional)" name="razorpay_webhook_secret" value={s.razorpay_webhook_secret} onChange={change} placeholder="••••••••••••" secret hint="For payment.captured webhook verification" />
                </div>
            </SectionCard>

            <SectionCard
                icon={CreditCard}
                title="Stripe (Global · USD / AED / INR)"
                hint="Get keys from Stripe Dashboard → Developers → API Keys."
                status={s.stripe_secret_key ? "connected" : "disconnected"}
            >
                <div className="grid sm:grid-cols-2 gap-4">
                    <SettingField label="Secret key" name="stripe_secret_key" value={s.stripe_secret_key} onChange={change} placeholder="sk_test_xxxxxxxxxxxxxx" secret hint="Server-side only. Never exposed to browser." />
                    <SettingField label="Publishable key" name="stripe_publishable_key" value={s.stripe_publishable_key} onChange={change} placeholder="pk_test_xxxxxxxxxxxxxx" />
                    <SettingField label="Webhook signing secret" name="stripe_webhook_secret" value={s.stripe_webhook_secret} onChange={change} placeholder="whsec_xxxxxxxxxxxxxx" secret />
                </div>
            </SectionCard>

            <SectionCard
                icon={Zap}
                title="Free WhatsApp via CallMeBot — zero per-message cost"
                hint="Use the free CallMeBot gateway for personal admin alerts (daily snapshot, late-case warnings). No Twilio credits needed. Limited to ONE recipient phone per API key — perfect for the admin number."
                status={s.callmebot_apikey && s.callmebot_phone ? "connected" : "disconnected"}
            >
                <div className="mb-4 text-[12.5px] text-slate-600 leading-relaxed bg-emerald-50/60 border border-emerald-200 rounded-lg px-3.5 py-3" data-testid="callmebot-howto">
                    <strong className="text-emerald-900">One-time setup (5 minutes):</strong>
                    <ol className="mt-1.5 ml-4 list-decimal space-y-0.5">
                        <li>From <code className="bg-white px-1 rounded">{s.callmebot_phone || "+97455060499"}</code> on WhatsApp, message <code className="bg-white px-1 rounded">+34 644 51 95 23</code> with the exact text:
                            <code className="block mt-1 bg-white px-2 py-1 rounded border border-emerald-200">I allow callmebot to send me messages</code>
                        </li>
                        <li>Wait up to 2 minutes. You&rsquo;ll receive a WhatsApp reply with your personal API key.</li>
                        <li>Paste it below and Save. Then test using the existing &quot;Send test WhatsApp&quot; button.</li>
                    </ol>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    <SettingField label="Recipient phone (E.164)" name="callmebot_phone" value={s.callmebot_phone} onChange={change} placeholder="+97455060499" hint="The phone that opted-in. CallMeBot keys are tied to a single recipient." />
                    <SettingField label="CallMeBot API key" name="callmebot_apikey" value={s.callmebot_apikey} onChange={change} placeholder="••••••" secret hint="6-7 digit numeric token you received via WhatsApp from CallMeBot." />
                </div>
                <div className="mt-4">
                    <label className="block">
                        <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500 mb-1.5">WhatsApp provider</div>
                        <select
                            name="whatsapp_provider"
                            data-testid="whatsapp-provider"
                            value={s.whatsapp_provider || "auto"}
                            onChange={(e) => change({ target: { name: "whatsapp_provider", value: e.target.value } })}
                            className="w-full sm:w-[320px] px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-cyan-500 outline-none text-[14px] bg-white"
                        >
                            <option value="auto">Auto — CallMeBot for matching phone, Twilio fallback</option>
                            <option value="callmebot">CallMeBot only (free)</option>
                            <option value="twilio">Twilio only</option>
                            <option value="off">Off — never send WhatsApp</option>
                        </select>
                        <div className="mt-1 text-[11.5px] text-slate-500">In <em>auto</em> mode, alerts to the CallMeBot phone go free; anything else (e.g. clinic notifications) falls back to Twilio if configured.</div>
                    </label>
                </div>
            </SectionCard>

            <SectionCard
                icon={MessageCircle}
                title="Twilio WhatsApp (paid · fallback for clinic notifications)"
                hint="Send daily lab summary + clinic status updates via WhatsApp. Get keys from console.twilio.com → Account Info. Only needed if you want non-admin WhatsApp messages."
                status={s.twilio_account_sid && s.twilio_auth_token ? "connected" : "disconnected"}
            >
                <div className="grid sm:grid-cols-2 gap-4">
                    <SettingField label="Account SID" name="twilio_account_sid" value={s.twilio_account_sid} onChange={change} placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxx" hint="Starts with 'AC'. Find at console.twilio.com" />
                    <SettingField label="Auth Token" name="twilio_auth_token" value={s.twilio_auth_token} onChange={change} placeholder="••••••••••••••••" secret hint="32-char token. Treat like a password." />
                    <SettingField label="WhatsApp 'From' number" name="twilio_whatsapp_from" value={s.twilio_whatsapp_from} onChange={change} placeholder="+14155238886" hint="E.164 format. Use Twilio's WhatsApp sandbox number for test, or your approved business number for prod." />
                    <SettingField label="Admin OTP phone" name="otp_phone" value={s.otp_phone} onChange={change} placeholder="+97455060499" hint="Destination for OTP codes when admin grants credits to a clinic. Changeable by super-admin only." />
                    <SettingField label="Low-balance warning threshold (QAR)" name="lowbalance_threshold_qar" value={s.lowbalance_threshold_qar} onChange={change} placeholder="1000" hint="When a clinic's credit balance falls below this, we WhatsApp+email them (max once / 24h). Skipped if auto top-up is enabled with a saved card." />
                    <SettingField label="Low-balance warning enabled" name="lowbalance_warning_enabled" value={s.lowbalance_warning_enabled} onChange={change} placeholder="true" hint="Set to 'false' to disable the automated low-balance nudge." />
                </div>
                <div className="mt-5 pt-5 border-t border-slate-100 flex flex-wrap items-end gap-3">
                    <label className="block flex-1 min-w-[220px]">
                        <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500 mb-1.5">Test phone (E.164)</div>
                        <input
                            data-testid="wa-test-recipient"
                            value={testWa}
                            onChange={(e) => setTestWa(e.target.value)}
                            placeholder="+97455060499"
                            className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-cyan-500 outline-none text-[14px]"
                        />
                    </label>
                    <button onClick={testWhatsapp} disabled={testingWa} data-testid="wa-test-btn" className="tr-btn-ghost !py-2.5">
                        {testingWa ? <Loader2 size={14} className="animate-spin" /> : <MessageCircle size={14} />}
                        Send test WhatsApp
                    </button>
                </div>
            </SectionCard>

            <SectionCard
                icon={Bell}
                title="Daily snapshot recipients"
                hint="Comma-separated emails and WhatsApp numbers that receive the daily lab summary."
            >
                <div className="grid sm:grid-cols-2 gap-4">
                    <SettingField label="Email recipients" name="daily_recipients" value={s.daily_recipients} onChange={change} placeholder="owner@clinic.com, manager@clinic.com" hint="Comma-separated. Orders inbox is always included automatically." />
                    <SettingField label="WhatsApp recipients" name="whatsapp_recipients" value={s.whatsapp_recipients} onChange={change} placeholder="+919999999999, +97455555555" hint="E.164 format, comma-separated. Requires Twilio above." />
                </div>
            </SectionCard>

            <SectionCard icon={Save} title="Defaults">
                <div className="grid sm:grid-cols-2 gap-4">
                    {/* Locked currency dropdown — replaces the old free-text INR
                        field. Prevents typos like `qar`/`Q.A.R`/`inr` sneaking
                        into records and driving reporting off the rails. Full
                        list mirrors the backend `fx.SUPPORTED_CURRENCIES`. */}
                    <label className="block" data-testid="setting-base-currency">
                        <div className="text-[11.5px] font-mono-tr tracking-[0.18em] uppercase text-slate-500 mb-1.5">Base reporting currency</div>
                        <select
                            name="currency_default"
                            value={(s.currency_default || "QAR").toUpperCase()}
                            onChange={(e) => change("currency_default", e.target.value)}
                            data-testid="setting-base-currency-select"
                            className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none text-[14px] bg-white font-mono-tr"
                        >
                            <optgroup label="Middle East">
                                <option value="QAR">QAR — Qatari Riyal</option>
                                <option value="SAR">SAR — Saudi Riyal</option>
                                <option value="AED">AED — UAE Dirham</option>
                                <option value="KWD">KWD — Kuwaiti Dinar</option>
                                <option value="BHD">BHD — Bahraini Dinar</option>
                                <option value="OMR">OMR — Omani Rial</option>
                                <option value="IQD">IQD — Iraqi Dinar</option>
                            </optgroup>
                            <optgroup label="Central Asia">
                                <option value="UZS">UZS — Uzbekistani Som</option>
                                <option value="KZT">KZT — Kazakhstani Tenge</option>
                                <option value="KGS">KGS — Kyrgyzstani Som</option>
                                <option value="TJS">TJS — Tajikistani Somoni</option>
                                <option value="TMT">TMT — Turkmenistani Manat</option>
                            </optgroup>
                            <optgroup label="Global reserve">
                                <option value="USD">USD — US Dollar</option>
                                <option value="EUR">EUR — Euro</option>
                                <option value="GBP">GBP — Pound Sterling</option>
                            </optgroup>
                            <optgroup label="South Asia">
                                <option value="INR">INR — Indian Rupee</option>
                                <option value="PKR">PKR — Pakistani Rupee</option>
                            </optgroup>
                        </select>
                        <div className="mt-1 text-[11.5px] text-slate-500">All new invoices, POs, and products default to this currency. Existing records keep their original currency — audit + convert them below.</div>
                    </label>
                    <SettingField label="GST rate (0.18 = 18%)" name="gst_rate" value={s.gst_rate} onChange={change} placeholder="0.18" />
                    <SettingField
                        label="Kanban WIP limit (per column / technician)"
                        name="kanban_wip_limit"
                        value={s.kanban_wip_limit}
                        onChange={change}
                        placeholder="5"
                        hint="Soft warning threshold. Cases-Kanban column counters and technician workload chips turn amber when in-flight cases reach this number. Set to 0 to disable."
                    />
                    {/* Lab-wide shade-guide preference (iter 60). New clinics
                        without an explicit preference inherit this. Existing
                        clinics are untouched — change them per-row from
                        Admin → Clients. */}
                    <label className="block" data-testid="setting-default-shade-system">
                        <div className="text-[11.5px] font-mono-tr tracking-[0.18em] uppercase text-slate-500 mb-1.5">Default shade guide for new clinics</div>
                        <select
                            name="default_shade_system_for_new_clients"
                            value={s.default_shade_system_for_new_clients || "classical"}
                            onChange={(e) => change({ target: { name: "default_shade_system_for_new_clients", value: e.target.value } })}
                            data-testid="setting-default-shade-system-select"
                            className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none text-[14px] bg-white"
                        >
                            <option value="classical">Vita Classical (A1…BL2)</option>
                            <option value="3d-master">Vita 3D-Master (0M1…5M3)</option>
                        </select>
                        <div className="mt-1 text-[11.5px] text-slate-500">Auto-applied to <strong>new</strong> clinics where the operator hasn&apos;t picked one. Existing clinics keep their current preference.</div>
                    </label>
                </div>
            </SectionCard>

            {/* Currency Audit — new panel. Depends on the base currency picked above.
                Lists every product/invoice/PO not in the base currency, and lets
                the admin either relabel (safe if data was tagged wrong) or convert
                with an FX rate (multiplies amounts, keeps full audit trail). */}
            <CurrencyAuditPanel baseCurrency={(s.currency_default || "QAR").toUpperCase()} s={s} change={change} />

            <SectionCard
                icon={Clock}
                title="Operations · Scheduled jobs"
                hint="Background jobs run on Asia/Kolkata time. Settings apply immediately — no restart needed."
                status={(s.auto_balance_enabled === 'true' || s.eom_announce_enabled === 'true') ? "connected" : "disconnected"}
            >
                <ScheduleBlock s={s} change={change} />
            </SectionCard>

            {/* iter 74 — Trusted-device management. Lets the signed-in user
                review + revoke their long-lived "stay signed in" sessions.
                Placed above Danger Zone since both are security-related,
                but this one is non-destructive. */}
            <TrustedDevicesPanel />

            {/* iter 61.2 — Danger zone. Platform-superadmin only.
                Two-factor wipe: confirm phrase + 12-digit key emailed to
                the admin. Rendered last so the operator scrolls past every
                regular setting before they see it. */}
            {user?.role === "superadmin" && <DangerZoneCard userEmail={user?.email} />}

            <div className="sticky bottom-4 bg-white rounded-2xl border border-slate-200 shadow-xl p-4 flex items-center justify-between" data-testid="settings-savebar">
                <div className="text-[13px] text-slate-600">
                    Changes are saved encrypted to the database and applied immediately.
                </div>
                <button onClick={save} disabled={saving} data-testid="settings-save-btn" className="tr-btn !py-2.5">
                    {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save settings</>}
                </button>
            </div>
        </div>
    );
}

function SectionCard({ icon: Icon, title, hint, status, children }) {
    return (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 lg:p-8">
            <div className="flex items-start justify-between mb-5">
                <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-700">
                        <Icon size={18} />
                    </div>
                    <div>
                        <div className="font-display text-xl tracking-tight text-slate-900">{title}</div>
                        {hint && <div className="text-[13px] text-slate-500 mt-1 max-w-2xl">{hint}</div>}
                    </div>
                </div>
                {status && (
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11.5px] font-medium ${
                        status === "connected" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
                    }`}>
                        {status === "connected" ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
                        {status === "connected" ? "Configured" : "Not configured"}
                    </span>
                )}
            </div>
            {children}
        </div>
    );
}

const ALL_DAYS = [
    { id: "mon", label: "Mon" },
    { id: "tue", label: "Tue" },
    { id: "wed", label: "Wed" },
    { id: "thu", label: "Thu" },
    { id: "fri", label: "Fri" },
    { id: "sat", label: "Sat" },
    { id: "sun", label: "Sun" },
];

function ScheduleBlock({ s, change }) {
    const balOn = (s.auto_balance_enabled || "false") === "true";
    const eomOn = (s.eom_announce_enabled || "true") === "true";
    const pulseOn = (s.weekly_pulse_enabled || "true") === "true";
    const daysCsv = s.auto_balance_days || "mon,tue,wed,thu,fri";
    const days = daysCsv.split(",").map((d) => d.trim()).filter(Boolean);

    const toggleDay = (d) => {
        const next = days.includes(d) ? days.filter((x) => x !== d) : [...days, d];
        const ordered = ALL_DAYS.map((x) => x.id).filter((x) => next.includes(x));
        change("auto_balance_days", ordered.join(","));
    };

    return (
        <div className="space-y-6" data-testid="schedule-block">
            {/* Auto-balance */}
            <div className="rounded-xl border border-slate-200 p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-3">
                        <div className="h-9 w-9 rounded-lg bg-cyan-50 text-cyan-700 flex items-center justify-center">
                            <Clock size={16} />
                        </div>
                        <div>
                            <div className="font-medium text-slate-900 text-[15px]">Daily auto-balance</div>
                            <div className="text-[12.5px] text-slate-500 max-w-md">Re-distributes today's accepted-but-undelivered cases across active technicians using load-balanced 3-tier routing.</div>
                        </div>
                    </div>
                    <ToggleSwitch
                        testid="toggle-auto-balance"
                        checked={balOn}
                        onChange={(v) => change("auto_balance_enabled", v ? "true" : "false")}
                    />
                </div>
                {balOn && (
                    <div className="mt-5 pt-5 border-t border-slate-100 grid sm:grid-cols-2 gap-4">
                        <div>
                            <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500 mb-1.5">Time of day · IST</div>
                            <div className="flex items-center gap-2">
                                <select
                                    data-testid="auto-balance-hour"
                                    value={s.auto_balance_hour || "10"}
                                    onChange={(e) => change("auto_balance_hour", e.target.value)}
                                    className="px-3 py-2.5 rounded-lg border border-slate-200 outline-none focus:border-cyan-500 text-[14px] font-mono-tr"
                                >
                                    {Array.from({ length: 24 }).map((_, h) => (
                                        <option key={`hour-${h}`} value={String(h)}>{String(h).padStart(2, "0")}</option>
                                    ))}
                                </select>
                                <span className="text-slate-400">:</span>
                                <select
                                    data-testid="auto-balance-minute"
                                    value={s.auto_balance_minute || "0"}
                                    onChange={(e) => change("auto_balance_minute", e.target.value)}
                                    className="px-3 py-2.5 rounded-lg border border-slate-200 outline-none focus:border-cyan-500 text-[14px] font-mono-tr"
                                >
                                    {[0, 15, 30, 45].map((m) => (
                                        <option key={m} value={String(m)}>{String(m).padStart(2, "0")}</option>
                                    ))}
                                </select>
                                <span className="text-[12px] font-mono-tr text-slate-500 ml-1">IST</span>
                            </div>
                        </div>
                        <div>
                            <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500 mb-1.5">Days of week</div>
                            <div className="flex flex-wrap gap-1.5" data-testid="auto-balance-days">
                                {ALL_DAYS.map((d) => {
                                    const on = days.includes(d.id);
                                    return (
                                        <button
                                            type="button"
                                            key={d.id}
                                            onClick={() => toggleDay(d.id)}
                                            data-testid={`day-${d.id}`}
                                            className={`px-2.5 py-1.5 rounded-full text-[12px] font-mono-tr tracking-wider uppercase border transition-colors ${on ? "bg-cyan-600 text-white border-cyan-600" : "bg-white text-slate-600 border-slate-200 hover:border-cyan-400"}`}
                                        >
                                            {d.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Employee of the Month */}
            <div className="rounded-xl border border-slate-200 p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-3">
                        <div className="h-9 w-9 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center">
                            <Trophy size={16} />
                        </div>
                        <div>
                            <div className="font-medium text-slate-900 text-[15px]">Employee of the Month announcement</div>
                            <div className="text-[12.5px] text-slate-500 max-w-md">
                                On the 1st of every month at 09:00 IST, automatically email + WhatsApp last month's winner (weighted by 50% revenue, 35% output, 15% on-time). PDF certificate link included.
                            </div>
                        </div>
                    </div>
                    <ToggleSwitch
                        testid="toggle-eom"
                        checked={eomOn}
                        onChange={(v) => change("eom_announce_enabled", v ? "true" : "false")}
                    />
                </div>
                <div className="mt-4 text-[12px] text-slate-500 leading-relaxed">
                    Sends to <span className="font-mono-tr">daily_recipients</span> emails and <span className="font-mono-tr">whatsapp_recipients</span> phones from above. Configure those to enable delivery.
                </div>
            </div>

            {/* Weekly Pulse */}
            <div className="rounded-xl border border-slate-200 p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-3">
                        <div className="h-9 w-9 rounded-lg bg-cyan-50 text-cyan-700 flex items-center justify-center">
                            <Clock size={16} />
                        </div>
                        <div>
                            <div className="font-medium text-slate-900 text-[15px]">Weekly Toothrocket Pulse</div>
                            <div className="text-[12.5px] text-slate-500 max-w-md">
                                Every Sunday at 18:00 IST, email a lab health digest (new cases, deliveries, revenue, late cases, top technicians, top products) to <span className="font-mono-tr">daily_recipients</span>.
                            </div>
                        </div>
                    </div>
                    <ToggleSwitch
                        testid="toggle-pulse"
                        checked={pulseOn}
                        onChange={(v) => change("weekly_pulse_enabled", v ? "true" : "false")}
                    />
                </div>
            </div>
        </div>
    );
}

function ToggleSwitch({ checked, onChange, testid }) {
    return (
        <button
            type="button"
            onClick={() => onChange(!checked)}
            data-testid={testid}
            role="switch"
            aria-checked={checked}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? "bg-cyan-600" : "bg-slate-300"}`}
        >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
        </button>
    );
}

const RESET_CONFIRM_PHRASE = "WIPE PRODUCTION DATA — I UNDERSTAND THIS CANNOT BE UNDONE";

/**
 * Danger Zone — two-factor production reset.
 *
 *   Step 1: POST /admin/production-reset/preview to show the operator
 *           what's about to be deleted.
 *   Step 2: POST /admin/production-reset/request-key — backend emails a
 *           12-digit cryptographic key to the calling superadmin's inbox.
 *   Step 3: Operator pastes the key + types the confirm phrase + clicks
 *           the red "WIPE" button. Backend validates both.
 *
 * Both factors are independent: a leaked session token AND a leaked
 * inbox would BOTH be needed to fire the wipe.
 */
function DangerZoneCard({ userEmail }) {
    const [plan, setPlan] = useState(null);
    const [keyRequested, setKeyRequested] = useState(false);
    const [keyMeta, setKeyMeta] = useState(null);
    const [resetKey, setResetKey] = useState("");
    const [phrase, setPhrase] = useState("");
    const [busy, setBusy] = useState(false);
    const [previewBusy, setPreviewBusy] = useState(false);
    const [done, setDone] = useState(null);

    const loadPreview = async () => {
        setPreviewBusy(true);
        try {
            const { data } = await axios.post(`${API}/admin/production-reset/preview`);
            setPlan(data.plan);
        } catch (e) {
            toast.error(e?.response?.data?.detail?.detail || e?.response?.data?.detail || "Could not load reset preview.");
        } finally {
            setPreviewBusy(false);
        }
    };

    const requestKey = async () => {
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/admin/production-reset/request-key`);
            setKeyRequested(true);
            setKeyMeta(data);
            toast.success(`A 12-digit reset key was emailed to ${data.delivered_to}.`);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const msg = (typeof detail === "object" && detail?.detail) || (typeof detail === "string" && detail) || "Could not request reset key.";
            // Iter 61.2 — when execute is locked from a previous run, offer the unlock.
            if (typeof detail === "object" && detail?.code === "already_done") {
                if (window.confirm("Reset was already used. Unlock for another run?")) {
                    return clearStamp();
                }
                return;
            }
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    const clearStamp = async () => {
        setBusy(true);
        try {
            await axios.post(`${API}/admin/production-reset/clear-stamp`);
            toast.success("Reset lock cleared. You can now request a key.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Unlock failed.");
        } finally {
            setBusy(false);
        }
    };

    const execute = async () => {
        if (phrase !== RESET_CONFIRM_PHRASE) {
            toast.error("Confirmation phrase does not match.");
            return;
        }
        if (!/^\d{12}$/.test(resetKey)) {
            toast.error("Reset key must be exactly 12 digits.");
            return;
        }
        if (!window.confirm("FINAL CONFIRM: wipe operational data? This cannot be undone.")) return;
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/admin/production-reset/execute`, {
                confirm_phrase: phrase,
                reset_key: resetKey,
            });
            setDone(data);
            toast.success("Reset complete. Backup created.");
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const code = typeof detail === "object" ? detail?.code : null;
            if (code === "key_mismatch") {
                toast.error(`Wrong key. ${detail.attempts_remaining} attempts left.`);
            } else if (code === "key_locked_out") {
                toast.error("Too many bad attempts. Request a new key.");
                setKeyRequested(false);
                setResetKey("");
            } else if (code === "key_expired") {
                toast.error("Key expired. Request a new one.");
                setKeyRequested(false);
            } else {
                toast.error((typeof detail === "object" ? detail?.detail : detail) || "Reset failed.");
            }
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/40 p-5" data-testid="danger-zone-done">
                <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center"><CheckCircle2 size={20} /></div>
                    <div>
                        <div className="font-display text-[17px] tracking-tight text-slate-900">Database wiped successfully</div>
                        <div className="text-[12.5px] text-slate-600 mt-0.5">Backup ID <span className="font-mono-tr text-slate-900">{done.backup?.id}</span> · {done.backup?.total_docs_backed_up} docs archived</div>
                    </div>
                </div>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[12px]">
                    {Object.entries(done.result || {}).filter(([k, v]) => k !== "preserved" && typeof v === "number" && v > 0).slice(0, 12).map(([col, n]) => (
                        <div key={col} className="rounded-lg bg-white border border-emerald-100 px-2.5 py-1.5">
                            <div className="font-mono-tr text-[10px] tracking-[0.16em] uppercase text-slate-500">{col}</div>
                            <div className="font-mono-tr text-slate-900 mt-0.5">{n} deleted</div>
                        </div>
                    ))}
                </div>
                <p className="mt-4 text-[11.5px] text-slate-500">Reload the page to refresh the rest of the admin. Backups live in <code className="font-mono-tr">/app/backups/</code>.</p>
            </div>
        );
    }

    return (
        <div className="rounded-2xl border-2 border-rose-200 bg-rose-50/30 p-5" data-testid="danger-zone">
            <div className="flex items-start gap-3 mb-4">
                <div className="h-10 w-10 rounded-xl bg-rose-100 text-rose-700 flex items-center justify-center shrink-0"><ShieldAlert size={20} /></div>
                <div className="min-w-0">
                    <div className="font-display text-[18px] tracking-tight text-slate-900">Danger zone · Reset database</div>
                    <p className="text-[12.5px] text-slate-600 mt-0.5">
                        Wipes all operational data (cases, invoices, doctors, ledgers, audit log) and preserves your tenant + admin users + product catalog + settings. <strong>Cannot be undone</strong>. A full ZIP backup is taken first.
                    </p>
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mb-4">
                {/* Step 1 - Preview */}
                <div className="rounded-xl bg-white border border-slate-200 p-3.5">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="h-5 w-5 rounded-full bg-slate-900 text-white text-[10px] font-mono-tr flex items-center justify-center">1</span>
                        <div className="text-[12.5px] font-medium text-slate-900">See what will be deleted</div>
                    </div>
                    <button onClick={loadPreview} disabled={previewBusy} data-testid="dz-preview-btn" className="tr-btn-ghost !py-1.5 !text-[12.5px] disabled:opacity-50">
                        {previewBusy ? <Loader2 size={12} className="animate-spin" /> : <AlertTriangle size={12} />} Show preview
                    </button>
                    {plan && (
                        <div className="mt-3 text-[11.5px] text-slate-600 space-y-1" data-testid="dz-preview-plan">
                            <div className="font-mono-tr"><span className="text-rose-700">{plan.total_to_delete}</span> docs to delete · <span className="text-emerald-700">{plan.total_to_keep}</span> kept</div>
                            <div className="text-slate-500">
                                {Object.entries(plan.to_delete || {}).filter(([, n]) => n > 0).map(([k, v]) => `${k}: ${v}`).join(" · ") || "(nothing to delete — DB already clean)"}
                            </div>
                        </div>
                    )}
                </div>

                {/* Step 2 - Request key */}
                <div className="rounded-xl bg-white border border-slate-200 p-3.5">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="h-5 w-5 rounded-full bg-slate-900 text-white text-[10px] font-mono-tr flex items-center justify-center">2</span>
                        <div className="text-[12.5px] font-medium text-slate-900">Email a reset key</div>
                    </div>
                    <button onClick={requestKey} disabled={busy} data-testid="dz-request-key-btn" className="tr-btn-ghost !py-1.5 !text-[12.5px] disabled:opacity-50">
                        {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}{" "}
                        {keyRequested ? "Re-send key" : "Send 12-digit key"}
                    </button>
                    {keyRequested && keyMeta && (
                        <div className="mt-3 text-[11.5px] text-slate-600">
                            Sent to <span className="font-mono-tr text-slate-900">{keyMeta.delivered_to || userEmail}</span> · expires in {Math.round((keyMeta.expires_in_seconds || 900) / 60)} min
                        </div>
                    )}
                </div>
            </div>

            {/* Step 3 - Execute */}
            <div className="rounded-xl bg-white border-2 border-rose-300 p-4" data-testid="dz-execute-card">
                <div className="flex items-center gap-2 mb-3">
                    <span className="h-5 w-5 rounded-full bg-rose-700 text-white text-[10px] font-mono-tr flex items-center justify-center">3</span>
                    <div className="text-[12.5px] font-medium text-slate-900">Confirm & execute</div>
                </div>
                <label className="block mb-3">
                    <div className="font-mono-tr text-[10.5px] tracking-[0.18em] uppercase text-slate-500 mb-1">12-digit key from your email</div>
                    <div className="relative">
                        <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            inputMode="numeric"
                            pattern="\d{12}"
                            maxLength={12}
                            value={resetKey}
                            onChange={(e) => setResetKey(e.target.value.replace(/\D/g, "").slice(0, 12))}
                            placeholder="000000000000"
                            data-testid="dz-key-input"
                            className="w-full pl-9 pr-3 py-2.5 text-center text-xl tracking-[8px] font-mono-tr rounded-lg border border-slate-200 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none"
                        />
                    </div>
                </label>
                <label className="block mb-3">
                    <div className="font-mono-tr text-[10.5px] tracking-[0.18em] uppercase text-slate-500 mb-1">
                        Type the phrase exactly: <span className="text-rose-700 font-medium">{RESET_CONFIRM_PHRASE}</span>
                    </div>
                    <input
                        type="text"
                        value={phrase}
                        onChange={(e) => setPhrase(e.target.value)}
                        placeholder="Paste or type the confirmation phrase"
                        data-testid="dz-phrase-input"
                        className="w-full px-3 py-2.5 text-[13.5px] font-mono-tr rounded-lg border border-slate-200 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none"
                    />
                </label>
                <button
                    onClick={execute}
                    disabled={busy || phrase !== RESET_CONFIRM_PHRASE || !/^\d{12}$/.test(resetKey)}
                    data-testid="dz-execute-btn"
                    className="w-full py-3 rounded-lg bg-rose-700 hover:bg-rose-800 text-white text-[13.5px] font-medium tracking-wide transition disabled:bg-slate-300 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                >
                    {busy ? <Loader2 size={15} className="animate-spin" /> : <ShieldAlert size={15} />}
                    WIPE DATABASE NOW
                </button>
                <p className="mt-2 text-[11px] text-slate-400 text-center">A full ZIP backup is taken automatically before deletion.</p>
            </div>
        </div>
    );
}
